/**
 * THE MONTHLY AWARD CALENDAR, in studio time, for the morning reminder
 * (netlify/functions/studentAwardsReminder.mjs), the Student Awards page, the
 * to-do it writes and the Jr Staff timeliness report alike.
 *
 * First written by Codex on PR #1247 (the month-end calendar, the transition
 * that leaves October 2026 and older rounds on their original deadline, and
 * the fix that chases the CURRENT award month after rollover); ported here and
 * extended with Bobbie's 2026-09-28 rules, verbatim: "these should be
 * automatic 5 days before the last day of the month and then a reminder again
 * to anyone who has not done it at the 2 day mark and the 1 day mark. then
 * dialy if they have note done it until it is done". The open day moved on
 * 2026-10-26, Bobbie, verbatim: "On staging we must get Oct students of the
 * month open 15 days before the end of the month so teachers can vote on all
 * of the monthly awards." (DECISIONS 157), and the same morning the to-do
 * came apart from it: "Can we do the TO DO AT THR 10 day mark?" (DECISIONS
 * 160). So from the October 2026 round on, a round OPENS fifteen days before
 * the month's last day and its to-do goes out TEN days before it; the 2 day,
 * 1 day and daily reminders, the due date and the close day are unchanged.
 *
 * TWO MONTHS PER ROUND, AND THEY ARE NOT THE SAME MONTH. Bobbie, 2026-09-28:
 * "they are nomiated for the previous month.... so they are nominating now
 * for Sept (at the end of Sept start of Oct)". A round is STORED under a
 * period key (the 'YYYY-MM' in every nomination, win and task id), and that
 * key is the month AFTER the one being recognized: picks made at the end of
 * September for September are stored under 2026-10, and the winner's
 * celebration runs during October. Nothing stored is re-keyed. Every word a
 * person reads names the RECOGNIZED month, through recognizedAwardMonthKey /
 * recognizedAwardMonthLabel below, which is the one mapping client and server
 * share.
 *
 * THE CALENDAR, for a round whose recognized month is M (period M+1):
 *   M's last day minus 15 the round OPENS: the page takes picks and a
 *                         Director's Add missing to-dos works
 *                         (AWARD_OPENS_DAYS_BEFORE below). Nobody is given a
 *                         to-do or told anything yet;
 *   minus 10              the to-do for anyone still missing and the first
 *                         reminder, with the steps
 *                         (AWARD_FIRST_REMINDER_DAYS_BEFORE below; minus 5
 *                         until 2026-10-26, then minus 15 for one morning,
 *                         DECISIONS 157 and 160);
 *   minus 2, minus 1      a reminder to anyone still missing;
 *   M's last day onward   a reminder every morning to anyone still missing,
 *                         through the round's CLOSE DAY, the 3rd of M+1 (Kim,
 *                         2026-09-28: "nominations for this can close down,
 *                         say  octr 3rd"; AWARD_NOMINATIONS_CLOSE_DAY below).
 *                         After it nobody can send a pick for M, so nobody is
 *                         asked to. The Director's "Check and post monthly
 *                         recipients" to-do exists once every pick is in, and
 *                         a pick she sends back before the close reopens the
 *                         round (netlify/functions/studentAwardsReminder.mjs).
 *
 * ONE ROUND AT A TIME. A round takes picks from its open day through its
 * close day, and the next one opens fifteen days before the end of the month,
 * nearly two weeks after the last closed, so at most one round is open on any
 * day (awardOpenRound below; tests/award-reminder-schedule.test.mjs walks
 * every day of 2026 to 2028). Kim, 2026-09-28: "we need to not see three
 * months at once".
 *
 * THE TRANSITION (Codex, #1247), and where it ends. Rounds stored under a
 * period before MONTH_END_AWARD_START_PERIOD keep the DUE DATE they were
 * seeded with (the last Monday of the recognized month, due five days later
 * at 22:00 UTC), so the Jr Staff timeliness report never re-grades an old
 * round. The September 2026 round (period 2026-10) was seeded that way on
 * September 28 with an October 3 deadline, and Bobbie then ruled, 2026-09-28
 * about 2pm Central, verbatim: "dancer of the month is due the last day of
 * each month!" So the month-end calendar starts WITH that round: it is due
 * Wednesday, September 30 at 11:59pm Central, reminded September 28 and 29
 * (2 days and 1 day before) and every morning from September 30. Its to-dos
 * went out on September 28, not on the 25th the month-end calendar of the
 * time (minus 5) would have used, and AWARD_ROUND_SENT_ON records that one
 * fact: it opened and went out that day, and the 2026-10-26 rules (open at
 * minus 15, to-do at minus 10) do not touch it. The morning reminder moves the
 * already-issued to-dos' stored deadline from October 3 to September 30
 * once (relabelSeptemberRoundTodos); nothing is re-keyed.
 *
 * ZERO IMPORTS on purpose, so lib/awardsDisplay.ts (which stays import-free
 * for its own tests) and a Netlify Function can both load it.
 */

export type AwardReminderStep = 'first' | 'reminder' | 'daily';

export interface CivilDate {
  year: number;
  month: number;
  day: number;
}

export interface AwardRoundToday {
  /** The round's stored period key, 'YYYY-MM' (the month AFTER the one recognized). */
  period: string;
  /**
   * What anyone still missing hears today. Null on a day inside the window
   * when nobody is due a reminder: a to-do missed by an earlier run is still
   * created, and its owner still gets the first reminder with the steps, but
   * nobody else hears anything.
   */
  step: AwardReminderStep | null;
  /** Whole days until the round's due date; negative once past. */
  daysLeft: number;
  /**
   * Has the round's to-do day (awardFirstReminderDateKey) come? From the open
   * day (awardOpensDateKey) until then this is false: the round takes picks,
   * and the morning reminder keeps its books (the roster, a second sign-in's
   * tick, a Director's check-and-post once every pick is in) but gives nobody
   * a to-do and tells nobody. True from the to-do day on, and always for a
   * round that opened the day its to-dos went out.
   */
  todosOut: boolean;
}

export const STUDIO_TIME_ZONE = 'America/Chicago';
/**
 * The first round on the month-end calendar: the September 2026 award, stored
 * under 2026-10 (Bobbie, 2026-09-28: "dancer of the month is due the last day
 * of each month!").
 */
export const MONTH_END_AWARD_START_PERIOD = '2026-10';
/**
 * The day a round's to-dos really went out, where that is not the calendar's
 * day. The September 2026 round was seeded on September 28 by the reminder
 * that came before this calendar, three days after its month-end first day.
 */
export const AWARD_ROUND_SENT_ON: Readonly<Record<string, string>> = Object.freeze({ '2026-10': '2026-09-28' });
/**
 * Days before the recognized month's last day that a round OPENS: the page,
 * the nominate lane, the MVP vote and a Director's Add missing to-dos all
 * take it from this day (awardOpensDateKey, awardOpenRound). Bobbie,
 * 2026-10-26: "open 15 days before the end of the month so teachers can vote
 * on all of the monthly awards" and "It should become available 15 days
 * before the end of the month!" (DECISIONS 157). Nobody is given a to-do on
 * this day; that is the next constant.
 */
export const AWARD_OPENS_DAYS_BEFORE = 15;
/**
 * Days before the recognized month's last day that the to-do and the first
 * reminder with the steps go out (awardNominationSeedDay,
 * awardFirstReminderDateKey, and the 'first' step of awardRoundPlanForDay).
 * 5 from Bobbie's 2026-09-28 rule; 15 for one morning (DECISIONS 157); 10
 * from Bobbie, 2026-10-26: "Can we do the TO DO AT THR 10 day mark?"
 * (DECISIONS 160). The September 2026 round is pinned by AWARD_ROUND_SENT_ON
 * and does not move.
 */
export const AWARD_FIRST_REMINDER_DAYS_BEFORE = 10;
/** Days before the last day that anyone still missing hears again. */
export const AWARD_REMINDER_DAYS_BEFORE: readonly number[] = [2, 1];
/** The transition calendar's chase, in days after its due date. */
const LEGACY_CHASE_DAYS = 14;
/** The transition calendar's due hour, UTC. */
const LEGACY_DUE_HOUR_UTC = 22;

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

export function periodKeyOf(year: number, month: number): string {
  return `${year}-${pad2(month)}`;
}

export function dateKeyOf(parts: CivilDate): string {
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

/** 1-based month. February is 28 or 29, read off the calendar itself. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

function periodParts(period: string): { year: number; month: number } | null {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(period);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) };
}

function civilFromUtcDate(date: Date): CivilDate {
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

function addDays(parts: CivilDate, days: number): CivilDate {
  return civilFromUtcDate(new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days)));
}

/* ============================================================================
 * The recognized month: the one mapping every label reads
 * ==========================================================================*/

/**
 * The month a round RECOGNIZES, 'YYYY-MM': the one before its stored period
 * key. 2026-10 recognizes September 2026; 2027-01 recognizes December 2026.
 * An unreadable key comes back unchanged, so a label shows it rather than
 * inventing a month.
 */
export function recognizedAwardMonthKey(period: string): string {
  const parts = periodParts(period);
  if (!parts) return period;
  const month = shiftMonth(parts.year, parts.month, -1);
  return periodKeyOf(month.year, month.month);
}

/** "September 2026" for the round stored under 2026-10, with no device-zone drift. */
export function recognizedAwardMonthLabel(period: string): string {
  const key = recognizedAwardMonthKey(period);
  const parts = periodParts(key);
  if (!parts) return period;
  return new Date(Date.UTC(parts.year, parts.month - 1, 1, 12)).toLocaleDateString('en-US', {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

/* ============================================================================
 * The calendar
 * ==========================================================================*/

export function isMonthEndAwardPeriod(period: string): boolean {
  return period >= MONTH_END_AWARD_START_PERIOD;
}

/** Last Monday of a 1-based month, the transition calendar's seed day. */
function legacySeedDay(year: number, month: number): number {
  const date = new Date(Date.UTC(year, month, 0));
  while (date.getUTCDay() !== 1) date.setUTCDate(date.getUTCDate() - 1);
  return date.getUTCDate();
}

/** The day of the recognized month the to-do goes out on the month-end calendar. */
export function awardNominationSeedDay(year: number, month: number): number {
  return daysInMonth(year, month) - AWARD_FIRST_REMINDER_DAYS_BEFORE;
}

/** The day of the recognized month a round opens on the month-end calendar. */
export function awardOpensDay(year: number, month: number): number {
  return daysInMonth(year, month) - AWARD_OPENS_DAYS_BEFORE;
}

/** The recognized month of a period, as year and 1-based month. */
function recognizedParts(period: string): { year: number; month: number } | null {
  const parts = periodParts(period);
  return parts ? shiftMonth(parts.year, parts.month, -1) : null;
}

/**
 * THE TO-DO DAY, 'YYYY-MM-DD': the day the morning reminder gives anyone
 * still missing the to-do and (on the month-end calendar) the first reminder
 * with the steps. Ten days before the recognized month's last day. Not the
 * day the round opens: that is awardOpensDateKey, five days earlier.
 */
export function awardFirstReminderDateKey(period: string): string {
  const recognized = recognizedParts(period);
  if (!recognized) return '';
  const sentOn = AWARD_ROUND_SENT_ON[period];
  if (sentOn) return sentOn;
  const day = isMonthEndAwardPeriod(period)
    ? awardNominationSeedDay(recognized.year, recognized.month)
    : legacySeedDay(recognized.year, recognized.month);
  return dateKeyOf({ ...recognized, day });
}

/**
 * THE OPEN DAY, 'YYYY-MM-DD': the first day a round takes picks, fifteen days
 * before the recognized month's last day (AWARD_OPENS_DAYS_BEFORE). A round
 * that went out on a day of its own (AWARD_ROUND_SENT_ON, the September 2026
 * round) and every transition round opened the day its to-dos went out.
 */
export function awardOpensDateKey(period: string): string {
  const recognized = recognizedParts(period);
  if (!recognized) return '';
  if (AWARD_ROUND_SENT_ON[period] || !isMonthEndAwardPeriod(period)) return awardFirstReminderDateKey(period);
  return dateKeyOf({ ...recognized, day: awardOpensDay(recognized.year, recognized.month) });
}

/**
 * The due date, 'YYYY-MM-DD': the recognized month's last day, or, for a
 * round seeded on the transition calendar, five days after its last Monday
 * (which can fall in the next month: August 31 plus five is September 5).
 */
export function awardDueDateKey(period: string): string {
  const recognized = recognizedParts(period);
  if (!recognized) return '';
  if (isMonthEndAwardPeriod(period)) {
    return dateKeyOf({ ...recognized, day: daysInMonth(recognized.year, recognized.month) });
  }
  return dateKeyOf(addDays({ ...recognized, day: legacySeedDay(recognized.year, recognized.month) }, 5));
}

/* ============================================================================
 * When nominations close, and which ONE round is open
 * ==========================================================================*/

/**
 * THE CLOSE DAY. Kim, 2026-09-28 (relayed by Bobbie, typed as she sent it):
 * "nominations for this can close down, say  octr 3rd. or whatever, because
 * at that point I'm already going to put out the newsletter and put up the
 * board. And so it doesn't help me to get nominations after that." So a
 * round's nominations close at the end of this day of the month AFTER the
 * month it recognizes, 11:59pm studio time: the September 2026 round closes
 * Saturday, October 3. "or whatever" means the day is hers to confirm; this
 * one number is the whole change if she picks another.
 */
export const AWARD_NOMINATIONS_CLOSE_DAY = 3;

/**
 * The last day a round takes nominations, 'YYYY-MM-DD': the close day of its
 * stored period month (which is the month after the one recognized). ''
 * for an unreadable key.
 */
export function awardNominationCloseDateKey(period: string): string {
  const parts = periodParts(period);
  if (!parts) return '';
  return dateKeyOf({ ...parts, day: AWARD_NOMINATIONS_CLOSE_DAY });
}

/**
 * The LAST instant of the close day in studio time, 11:59:59.999pm, as epoch
 * milliseconds. awardOpenRound decides by the studio date, so the whole of
 * the 11:59pm minute is still open there; this agrees with it (refute of
 * #1266, P3: 11:59:00.000 treated a pick sent at 11:59:30pm as after the
 * close while the round still took it).
 */
export function awardNominationClosesAtMs(period: string): number {
  const key = awardNominationCloseDateKey(period);
  return key ? studioEndOfDayMs(key) + 59_999 : Number.NaN;
}

/**
 * The LAST instant of the due day in studio time, 11:59:59.999pm, as epoch
 * milliseconds: what "sent by the due date" means on the Director's on-time
 * report. The same end-of-day rule awardNominationClosesAtMs uses, so a pick
 * sent at 11:59:30pm on the due day is on time (refute item 10 on #1283:
 * the report compared against the to-do's 11:59:00.000 and called it late).
 * Every round, the transition ones included, since the report speaks of
 * the due DATE.
 */
export function awardDueEndsAtMs(period: string): number {
  const key = awardDueDateKey(period);
  return key ? studioEndOfDayMs(key) + 59_999 : Number.NaN;
}

/**
 * The last morning of the daily chase, 'YYYY-MM-DD' ('' for an unreadable
 * key). A month-end round chases anyone still missing every morning through
 * its close day and then stops, because Kim has already put out the
 * newsletter and put up the board (AWARD_NOMINATIONS_CLOSE_DAY). This used to
 * be null, "until her pick is in", from Bobbie's "dialy if they have note done
 * it until it is done"; Kim's close day is the end of "until it is done" now,
 * since a pick after it can no longer be sent. The transition rounds, all long
 * over, keep the fixed chase they were seeded with, so nothing old wakes up.
 * The return type keeps null so older callers still read it safely.
 */
export function awardChaseLastDateKey(period: string): string | null {
  if (!periodParts(period)) return '';
  if (isMonthEndAwardPeriod(period)) return awardNominationCloseDateKey(period);
  const [year, month, day] = awardDueDateKey(period).split('-').map(Number);
  return dateKeyOf(addDays({ year, month, day }, LEGACY_CHASE_DAYS));
}

function studioOffsetMinutesAt(ms: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: STUDIO_TIME_ZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(ms));
  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const hour = Number(map.hour) === 24 ? 0 : Number(map.hour);
  const asIfUtc = Date.UTC(
    Number(map.year), Number(map.month) - 1, Number(map.day), hour, Number(map.minute), Number(map.second)
  );
  return (asIfUtc - ms) / 60_000;
}

/**
 * 11:59pm studio time on a 'YYYY-MM-DD' day, as epoch milliseconds. The
 * offset is read at that evening itself, so a due date on the night the
 * clocks change (Nov 1, 2026) lands on the right hour.
 */
export function studioEndOfDayMs(dateKey: string): number {
  const [year, month, day] = dateKey.split('-').map(Number);
  const guess = Date.UTC(year, month - 1, day, 23, 59, 0);
  return guess - studioOffsetMinutesAt(guess) * 60_000;
}

/**
 * The instant a round's to-do is due. Month-end rounds: 11:59pm studio time
 * on the recognized month's last day. Transition rounds keep the 22:00 UTC
 * they were seeded with, so nothing already out is re-graded.
 */
export function awardTaskDueDate(period: string): Date {
  const key = awardDueDateKey(period);
  if (!key) return new Date(Number.NaN);
  if (isMonthEndAwardPeriod(period)) return new Date(studioEndOfDayMs(key));
  const [year, month, day] = key.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, LEGACY_DUE_HOUR_UTC));
}

/** The studio's calendar day at one instant, from the zone database (DST included). */
export function studioDateParts(date: Date): CivilDate {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: STUDIO_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return { year: Number(map.year), month: Number(map.month), day: Number(map.day) };
}

function daysBetween(fromKey: string, toKey: string): number {
  const [fy, fm, fd] = fromKey.split('-').map(Number);
  const [ty, tm, td] = toKey.split('-').map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

/**
 * One round's plan for one studio day, or null when that round is not live.
 * The round is live from its OPEN day (awardOpensDateKey) through its chase's
 * last morning: the close day for a month-end round, the old fixed chase for
 * a transition round. Whether anyone still owes it is the reminder's
 * question, not the calendar's. Until its to-do day (awardFirstReminderDateKey)
 * `todosOut` is false and there is no step: the morning reminder gives no
 * to-do and tells nobody. Which step is due after that depends on the
 * calendar it was seeded on.
 */
export function awardRoundPlanForDay(period: string, today: CivilDate): AwardRoundToday | null {
  const todayKey = dateKeyOf(today);
  const opens = awardOpensDateKey(period);
  const first = awardFirstReminderDateKey(period);
  const due = awardDueDateKey(period);
  const last = awardChaseLastDateKey(period);
  if (!opens || !first || !due || last === '') return null;
  if (todayKey < opens || (last !== null && todayKey > last)) return null;
  const daysLeft = daysBetween(todayKey, due);
  if (todayKey < first) return { period, step: null, daysLeft, todosOut: false };
  if (isMonthEndAwardPeriod(period)) {
    let step: AwardReminderStep | null = null;
    if (daysLeft === AWARD_FIRST_REMINDER_DAYS_BEFORE) step = 'first';
    else if (AWARD_REMINDER_DAYS_BEFORE.includes(daysLeft)) step = 'reminder';
    else if (daysLeft <= 0) step = 'daily';
    return { period, step, daysLeft, todosOut: true };
  }
  // Transition calendar (rounds before the September 2026 award, all long
  // over): the round keeps the due date its to-dos were seeded with, and the
  // cadence runs against THAT date: a reminder 2 days and 1 day before, then
  // every morning from the due date itself until the pick is in or the chase
  // ends. The last Monday seeded it, so there is no first reminder to send; a
  // to-do created late still gets one with the steps.
  let step: AwardReminderStep | null = null;
  if (AWARD_REMINDER_DAYS_BEFORE.includes(daysLeft)) step = 'reminder';
  else if (daysLeft <= 0) step = 'daily';
  return { period, step, daysLeft, todosOut: true };
}

/**
 * Which rounds are live on one studio calendar day, newest first: every
 * month-end round from the September 2026 award through the one recognizing
 * THIS month (once its open day has come), and a transition round only
 * inside its old fixed chase. A day before September 28, 2026 returns
 * nothing and the reminder reads nothing; from then on the September round is
 * always on the list, and the reminder asks each round who still owes it.
 */
export function awardRoundsForDay(today: CivilDate): AwardRoundToday[] {
  const current = periodKeyOf(today.year, today.month);
  const next = shiftMonth(today.year, today.month, 1);
  const nextPeriod = periodKeyOf(next.year, next.month);
  const rounds: AwardRoundToday[] = [];
  const start = periodParts(MONTH_END_AWARD_START_PERIOD);
  if (start) {
    const startIndex = start.year * 12 + (start.month - 1);
    for (let index = next.year * 12 + (next.month - 1); index >= startIndex; index -= 1) {
      const period = periodKeyOf(Math.floor(index / 12), (index % 12) + 1);
      const plan = awardRoundPlanForDay(period, today);
      if (plan) rounds.push(plan);
    }
  }
  for (const period of [nextPeriod, current]) {
    if (isMonthEndAwardPeriod(period)) continue;
    const plan = awardRoundPlanForDay(period, today);
    if (plan) rounds.push(plan);
  }
  return rounds;
}

/**
 * WHICH ROUND IS OPEN, the one answer the page, the save, the server lane and
 * the stray-pick merge all read. Kim, 2026-09-28: "we need to not see three
 * months at once ... So right now, the only one that should be showing is
 * September 2026".
 *
 * A round is open from its open day (awardOpensDateKey: fifteen days before
 * the recognized month ends from the October 2026 round on, Bobbie
 * 2026-10-26, so October 16 for the October 2026 round; September 28 for the
 * pinned September 2026 round) through its close day
 * (AWARD_NOMINATIONS_CLOSE_DAY of the next month), both in studio time. Its
 * to-do day (`todoDateKey`, awardFirstReminderDateKey, ten days before) falls
 * inside that window and opens nothing. At most one round is open on any
 * day: the one
 * stored under this month (recognizing last month) until it closes on the
 * 3rd, then nothing until the one stored under next month opens late in this
 * month. Between the two, `period` is the round coming up and `open` is
 * false, so a nominator is told when it opens instead of being handed a form.
 *
 * `lastClosedPeriod` is the most recent round whose nominations have closed,
 * and `reviewPeriod` is where Kim's review lands: the open round, or while
 * none is open, the one that just closed (the list she is turning into the
 * newsletter and the board).
 */
export interface AwardOpenRound {
  period: string;
  open: boolean;
  /** The first day it takes picks (awardOpensDateKey). */
  opensDateKey: string;
  /** The morning anyone still missing gets the to-do (awardFirstReminderDateKey). */
  todoDateKey: string;
  closesDateKey: string;
  lastClosedPeriod: string;
  reviewPeriod: string;
}

export function awardOpenRound(now: Date): AwardOpenRound {
  const today = studioDateParts(now);
  const todayKey = dateKeyOf(today);
  const thisPeriod = periodKeyOf(today.year, today.month);
  const next = shiftMonth(today.year, today.month, 1);
  const nextPeriod = periodKeyOf(next.year, next.month);
  const previous = shiftMonth(today.year, today.month, -1);
  const previousPeriod = periodKeyOf(previous.year, previous.month);
  const window = (period: string) => ({
    opensDateKey: awardOpensDateKey(period),
    todoDateKey: awardFirstReminderDateKey(period),
    closesDateKey: awardNominationCloseDateKey(period),
  });
  // This month's period recognizes last month and is open through the 3rd.
  if (todayKey <= awardNominationCloseDateKey(thisPeriod)) {
    return {
      period: thisPeriod,
      open: todayKey >= awardOpensDateKey(thisPeriod),
      ...window(thisPeriod),
      lastClosedPeriod: previousPeriod,
      reviewPeriod: thisPeriod,
    };
  }
  const open = todayKey >= awardOpensDateKey(nextPeriod);
  return {
    period: nextPeriod,
    open,
    ...window(nextPeriod),
    lastClosedPeriod: thisPeriod,
    reviewPeriod: open ? nextPeriod : thisPeriod,
  };
}

/** Is this stored period the one round taking nominations at `now`? */
export function awardRoundTakesNominations(period: string, now: Date): boolean {
  const round = awardOpenRound(now);
  return round.open && round.period === period;
}

/** Plain words for a round that is not taking picks, naming its month. */
export function awardRoundClosedMessage(period: string, now: Date): string {
  const round = awardOpenRound(now);
  const month = recognizedAwardMonthLabel(period);
  if (round.open && round.period !== period) {
    return `Nominations for ${month} are not open. A Director is collecting ${recognizedAwardMonthLabel(round.period)} picks now, so send yours there.`;
  }
  if (period === round.period && !round.open) {
    return `Nominations for ${month} open ${awardLongDateLabel(round.opensDateKey)}.`;
  }
  return `Nominations for ${month} are closed.`;
}

/**
 * The first day of the month a round RECOGNIZES, 'YYYY-MM-01'. "Who teaches"
 * for a round is asked as of this day (lib/awardNominators.ts
 * awardTeachingAsOfKey), so a class whose term ends inside the window still
 * counts for the month it was taught in.
 */
export function recognizedMonthFirstDateKey(period: string): string {
  const recognized = recognizedParts(period);
  return recognized ? dateKeyOf({ ...recognized, day: 1 }) : '';
}

/* ============================================================================
 * Date labels
 * ==========================================================================*/

/** "Saturday, October 31" for a 'YYYY-MM-DD' key, with no device-zone drift. */
export function awardLongDateLabel(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12)).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
}

/** "Oct 31" for a 'YYYY-MM-DD' key. */
export function awardShortDateLabel(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}
